import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Play, FileText, Image, Shield, Code, Calculator } from 'lucide-react';
import { getAllTools } from '../data/tools';

interface ToolInstructions {
  id: string;
  steps: string[];
  tips: string[];
  supportedFormats?: string[];
}

const toolInstructions: Record<string, ToolInstructions> = {
  // Image Tools - Complete Coverage
  'webp-to-jpg': {
    id: 'webp-to-jpg',
    steps: [
      'Click the upload area or drag and drop your WEBP files from your computer',
      'Select one or multiple WEBP images using the file browser (Ctrl+click for multiple selection)',
      'Preview the selected images in the tool interface',
      'Optionally adjust quality settings (70-95% recommended for different use cases)',
      'Click "Convert to JPG" to start the client-side conversion process',
      'Wait for processing to complete (progress bar will show status)',
      'Download individual converted files or use "Download All" for batch download'
    ],
    tips: [
      'JPG compression is lossy and may reduce file size but can decrease quality',
      'For web use, 70-80% quality provides good balance of size and quality',
      'For print use, 90-95% quality maintains high image quality',
      'Multiple files can be converted simultaneously for batch processing',
      'Original WEBP files remain completely untouched on your device',
      'Conversion happens locally - no files are uploaded to servers'
    ],
    supportedFormats: ['WEBP']
  },
  'png-to-jpg': {
    id: 'png-to-jpg',
    steps: [
      'Upload your PNG files using the file selector or drag-and-drop interface',
      'Choose background color for transparent areas (default: white, or select custom color)',
      'Adjust JPG quality settings based on your needs (80-95% recommended)',
      'Preview the conversion result to check transparency handling',
      'Click "Convert" to process the images locally in your browser',
      'Download the converted JPG files individually or as a batch'
    ],
    tips: [
      'PNG transparency will be filled with your selected background color',
      'JPG format doesn\'t support transparency - choose background color carefully',
      'Lower quality settings (60-70%) create smaller files for web use',
      'Higher quality settings (90-95%) maintain image quality for print',
      'Consider the final use case when choosing background color',
      'White background works best for most documents and photos'
    ],
    supportedFormats: ['PNG']
  },
  'jpg-to-png': {
    id: 'jpg-to-png',
    steps: [
      'Upload JPG/JPEG files using the upload interface',
      'Review the images in the preview panel',
      'Choose PNG compression level (higher compression = smaller files)',
      'Click "Convert to PNG" to start the lossless conversion',
      'Download the high-quality PNG files'
    ],
    tips: [
      'PNG conversion is lossless - no quality degradation',
      'PNG files will be larger than JPG but with perfect quality',
      'Ideal for images that need transparency editing later',
      'Perfect for logos, graphics, and images requiring transparency',
      'No background color needed since JPG has no transparency'
    ],
    supportedFormats: ['JPG', 'JPEG']
  },
  'image-compressor': {
    id: 'image-compressor',
    steps: [
      'Upload images you want to compress (JPG, PNG, WEBP supported)',
      'Adjust quality slider based on your requirements (lower = smaller file, higher = better quality)',
      'Set maximum width/height dimensions if you need resizing',
      'Preview the compression results and file size reduction',
      'Fine-tune settings if needed based on the preview',
      'Click "Compress" to process your images locally',
      'Download compressed images individually or all at once using batch download'
    ],
    tips: [
      'Start with 80% quality for a good balance of size and quality',
      'For web images: 60-70% quality is usually sufficient',
      'For professional use: 85-95% quality maintains high standards',
      'Preview compression results before downloading',
      'Batch processing saves time when compressing multiple images',
      'Original images are never modified or uploaded to servers',
      'Compression happens entirely in your browser for privacy'
    ],
    supportedFormats: ['JPG', 'PNG', 'WEBP', 'BMP']
  },
  'image-cropper': {
    id: 'image-cropper',
    steps: [
      'Upload an image file to the cropper tool',
      'Use mouse to drag and resize the crop selection area',
      'Adjust crop boundaries by dragging corner handles',
      'Set specific dimensions if needed using the dimension inputs',
      'Preview the crop result in real-time',
      'Click "Crop Image" to apply the selection',
      'Download the cropped image file'
    ],
    tips: [
      'Maintain aspect ratio by holding Shift while resizing',
      'Use preset aspect ratios for common formats (1:1, 16:9, 4:3)',
      'Zoom in for precise crop selection on detailed images',
      'Preview shows exact crop result before processing',
      'Cropped images maintain original quality in the selected area'
    ],
    supportedFormats: ['JPG', 'PNG', 'WEBP', 'BMP']
  },
  'image-to-base64': {
    id: 'image-to-base64',
    steps: [
      'Upload your image file using the file selector',
      'Wait for the image to be processed and converted to Base64',
      'Copy the Base64 string from the output field',
      'Use the Base64 string in your applications or code',
      'Optionally download the Base64 as a text file'
    ],
    tips: [
      'Base64 strings are longer than original file sizes',
      'Useful for embedding images in HTML, CSS, or JSON',
      'Great for email templates and data URIs',
      'Copy the complete string including the data URL prefix',
      'Base64 conversion is perfect for small images and icons'
    ],
    supportedFormats: ['JPG', 'PNG', 'WEBP', 'GIF', 'SVG']
  },
  'base64-to-image': {
    id: 'base64-to-image',
    steps: [
      'Paste your Base64 string into the input field',
      'Include the data URL prefix (data:image/...) if available',
      'Preview the decoded image to verify correctness',
      'Choose the output format if multiple options are available',
      'Click "Convert" to decode the Base64 to image',
      'Download the resulting image file'
    ],
    tips: [
      'Ensure Base64 string is complete and not truncated',
      'Include the full data URL for best results',
      'Preview helps verify the Base64 data is valid',
      'Works with all common image formats encoded in Base64',
      'Useful for extracting images from JSON data or web sources'
    ],
    supportedFormats: ['Base64 encoded images']
  },

  // PDF Tools - Comprehensive Coverage
  'pdf-merger': {
    id: 'pdf-merger',
    steps: [
      'Upload multiple PDF files you want to merge using drag-and-drop or file selection',
      'Review the list of uploaded PDFs in the merge queue',
      'Drag and drop files to reorder them in your preferred sequence',
      'Preview file information including page counts and file sizes',
      'Use the arrow buttons to fine-tune file order if needed',
      'Click "Merge PDFs" to combine them into a single document',
      'Wait for processing (may take time for large PDFs)',
      'Download the merged PDF document to your device'
    ],
    tips: [
      'File order in the list determines the order in the merged PDF',
      'Drag files up or down to reorder before merging',
      'Check individual file sizes to estimate final merged size',
      'Merged PDFs maintain original quality and formatting',
      'Bookmarks and metadata from original files are preserved',
      'Password-protected PDFs need to be unlocked before merging',
      'Large PDFs may take several minutes to merge depending on device performance'
    ],
    supportedFormats: ['PDF']
  },
  'jpg-to-pdf': {
    id: 'jpg-to-pdf',
    steps: [
      'Upload one or multiple JPG/JPEG images',
      'Arrange images in the desired order using drag-and-drop',
      'Choose page orientation (portrait or landscape) for each image',
      'Select page size (A4, Letter, or custom dimensions)',
      'Adjust image positioning and scaling options',
      'Preview the PDF layout before generation',
      'Click "Create PDF" to generate the document',
      'Download the resulting PDF file'
    ],
    tips: [
      'Images are embedded at full quality unless compressed',
      'Choose orientation based on image dimensions for best fit',
      'A4 size works well for most document purposes',
      'Multiple images create a multi-page PDF document',
      'Maintain image order by arranging before conversion',
      'Consider image resolution for print vs digital use'
    ],
    supportedFormats: ['JPG', 'JPEG']
  },
  'pdf-metadata': {
    id: 'pdf-metadata',
    steps: [
      'Upload a PDF file to analyze',
      'Wait for metadata extraction to complete',
      'Review document information including title, author, subject',
      'Check creation and modification dates',
      'View page count and file size information',
      'Copy metadata information if needed for documentation'
    ],
    tips: [
      'Metadata shows document properties set by the creator',
      'Useful for document management and organization',
      'Creation date shows when PDF was originally generated',
      'Some PDFs may have limited or no metadata',
      'Page count helps verify document completeness'
    ],
    supportedFormats: ['PDF']
  },

  // Text Tools - Complete Coverage
  'text-analyzer': {
    id: 'text-analyzer',
    steps: [
      'Paste or type your text in the large input text area',
      'Analysis updates automatically as you type or paste content',
      'View word count, character count (with and without spaces)',
      'Check paragraph count and sentence statistics',
      'Review reading time estimates for different reading speeds',
      'Examine readability scores and grade level analysis',
      'Copy specific statistics for use in other applications'
    ],
    tips: [
      'Real-time analysis updates immediately as you edit text',
      'Reading time is calculated based on average reading speeds',
      'Readability scores help assess content complexity',
      'Word frequency analysis shows most common terms',
      'Copy individual statistics or the complete analysis',
      'Great for meeting document requirements and word limits',
      'Useful for content writing, academic papers, and reports'
    ],
    supportedFormats: ['Plain Text', 'Rich Text', 'Markdown']
  },
  'text-capitalizer': {
    id: 'text-capitalizer',
    steps: [
      'Enter or paste text in the input field',
      'Choose capitalization style: Title Case, Sentence Case, etc.',
      'Preview the capitalized result in real-time',
      'Fine-tune specific words if needed',
      'Copy the capitalized text to clipboard',
      'Use swap function to move output back to input for further editing'
    ],
    tips: [
      'Title Case capitalizes major words, ignoring articles and prepositions',
      'Sentence Case capitalizes only the first word of sentences',
      'Different styles work better for different content types',
      'Preview helps ensure correct capitalization before copying',
      'Swap function allows iterative editing and refinement'
    ],
    supportedFormats: ['Plain Text']
  },
  'duplicate-remover': {
    id: 'duplicate-remover',
    steps: [
      'Paste text with duplicate lines into the input area',
      'Choose whether to preserve original line order',
      'Select case-sensitive or case-insensitive comparison',
      'Click "Remove Duplicates" to process the text',
      'Review the cleaned text with duplicates removed',
      'Copy the result or download as a text file'
    ],
    tips: [
      'Case-insensitive removal treats "Apple" and "apple" as duplicates',
      'Preserving order keeps the first occurrence of each unique line',
      'Great for cleaning up lists, email addresses, and data sets',
      'Whitespace differences are considered when comparing lines',
      'Empty lines can be optionally removed during processing'
    ],
    supportedFormats: ['Plain Text', 'Lists']
  },

  // Encryption Tools - Detailed Coverage
  'base64-encoder': {
    id: 'base64-encoder',
    steps: [
      'Enter your text in the input field',
      'Choose "Encode" mode to convert text to Base64',
      'Choose "Decode" mode to convert Base64 back to text',
      'Click the convert button to process your text locally',
      'Copy the result from the output field using the copy button',
      'Use the "Swap" button to move output back to input for reverse operation',
      'Clear fields using the clear button when starting fresh'
    ],
    tips: [
      'Base64 is encoding, not encryption - data can be easily decoded',
      'Useful for data transmission where binary data isn\'t supported',
      'Base64 encoded text is approximately 33% larger than original',
      'Perfect for embedding binary data in JSON or XML',
      'URL-safe Base64 variants replace + and / with - and _',
      'All encoding/decoding happens locally for privacy'
    ],
    supportedFormats: ['Text', 'Base64']
  },
  'md5-hash': {
    id: 'md5-hash',
    steps: [
      'Enter text or data you want to hash',
      'Click "Generate MD5 Hash" to create the hash',
      'Copy the 32-character hexadecimal MD5 hash',
      'Use the hash for checksums or verification purposes'
    ],
    tips: [
      'MD5 is NOT cryptographically secure - use for checksums only',
      'Same input always produces the same MD5 hash',
      'MD5 hashing is one-way - cannot be reversed to original text',
      'Commonly used for file integrity verification',
      'Consider SHA-256 for security-sensitive applications'
    ],
    supportedFormats: ['Text', 'Any string data']
  },
  'sha256-hash': {
    id: 'sha256-hash',
    steps: [
      'Input the text or data to be hashed',
      'Click "Generate SHA-256 Hash" for processing',
      'Copy the 64-character hexadecimal SHA-256 hash',
      'Use for secure verification and integrity checking'
    ],
    tips: [
      'SHA-256 is cryptographically secure and recommended for security',
      'Produces a 256-bit hash output (64 hex characters)',
      'Widely used in blockchain, certificates, and security applications',
      'Even small input changes result in completely different hashes',
      'Suitable for password storage when combined with salting'
    ],
    supportedFormats: ['Text', 'Any string data']
  },

  // Developer Tools - Comprehensive Coverage
  'csv-json-converter': {
    id: 'csv-json-converter',
    steps: [
      'Choose conversion direction: CSV to JSON or JSON to CSV',
      'Paste your CSV data with proper column headers',
      'Or paste JSON array data for reverse conversion',
      'Configure delimiter options (comma, semicolon, tab)',
      'Set quote character preferences if needed',
      'Click "Convert" to transform the data',
      'Copy the converted result or download as file'
    ],
    tips: [
      'First row of CSV should contain column headers',
      'JSON arrays work best for CSV conversion',
      'Handle special characters by adjusting quote settings',
      'Preview conversion results before downloading',
      'Large datasets may take a moment to process',
      'Ensure data structure is consistent for best results'
    ],
    supportedFormats: ['CSV', 'JSON']
  },
  'xml-formatter': {
    id: 'xml-formatter',
    steps: [
      'Paste unformatted or minified XML into the input area',
      'Choose indentation style (spaces or tabs)',
      'Set indentation size (2 or 4 spaces recommended)',
      'Click "Format XML" to beautify the structure',
      'Review the formatted, readable XML output',
      'Copy formatted XML or download as file'
    ],
    tips: [
      'Formatting makes XML more readable and maintainable',
      'Validates XML structure during formatting process',
      'Consistent indentation improves code readability',
      'Preserves all data while improving presentation',
      'Helpful for debugging and code review processes'
    ],
    supportedFormats: ['XML', 'Malformed XML']
  },

  // Converter Tools - Full Coverage
  'qr-generator': {
    id: 'qr-generator',
    steps: [
      'Enter text, URL, or data you want to encode in the QR code',
      'Choose QR code size (small, medium, large, or custom pixels)',
      'Select error correction level (L, M, Q, H for different reliability)',
      'Choose colors for foreground and background if desired',
      'Click "Generate QR Code" to create the code',
      'Preview the generated QR code',
      'Download as PNG, JPG, or SVG format'
    ],
    tips: [
      'Higher error correction allows reading even if partially damaged',
      'Larger sizes work better for printing and distant scanning',
      'URLs should include http:// or https:// for proper linking',
      'Test QR codes with multiple devices before final use',
      'SVG format provides scalable vector graphics for any size',
      'Keep text concise - too much data makes codes hard to scan'
    ],
    supportedFormats: ['Text', 'URLs', 'Any string data']
  },
  'number-base-converter': {
    id: 'number-base-converter',
    steps: [
      'Enter a number in the source base (binary, octal, decimal, or hexadecimal)',
      'Select the source number base from the dropdown',
      'Choose the target base you want to convert to',
      'Click "Convert" to see results in all supported bases',
      'Copy specific base results or view all conversions simultaneously',
      'Use the swap function to reverse conversion direction'
    ],
    tips: [
      'Binary (base 2) uses only 0 and 1 digits',
      'Octal (base 8) uses digits 0-7',
      'Decimal (base 10) is the standard number system',
      'Hexadecimal (base 16) uses 0-9 and A-F',
      'Large numbers may have long representations in smaller bases',
      'Useful for programming, computer science, and mathematics'
    ],
    supportedFormats: ['Binary', 'Octal', 'Decimal', 'Hexadecimal']
  }
};

const categoryInstructions = {
  image: {
    icon: <Image className="h-6 w-6" />,
    title: 'Image Tools',
    description: 'Convert, compress, and edit images locally in your browser',
    generalSteps: [
      'Upload your image file(s) using drag & drop or file selection',
      'Configure any settings specific to the tool (quality, format, dimensions)',
      'Process your image(s) - all computation happens in your browser',
      'Download the processed image(s) to your device'
    ],
    tips: [
      'All image processing happens locally - your files never leave your device',
      'Supported formats vary by tool, but generally include JPG, PNG, WEBP',
      'Large images may take longer to process depending on your device',
      'Some tools support multiple files while others process one file at a time'
    ]
  },
  pdf: {
    icon: <FileText className="h-6 w-6" />,
    title: 'PDF Tools',
    description: 'Merge, convert, and analyze PDF documents securely',
    generalSteps: [
      'Select your PDF file(s) or other documents to process',
      'Use tool-specific options (merge order, conversion settings, etc.)',
      'Click the process button to perform the operation',
      'Download your processed PDF file'
    ],
    tips: [
      'PDF processing maintains document quality and formatting',
      'Password-protected PDFs may require additional handling',
      'File size limits depend on your browser\'s memory capacity',
      'Complex PDFs with forms or annotations are fully supported'
    ]
  },
  text: {
    icon: <FileText className="h-6 w-6" />,
    title: 'Text Tools',
    description: 'Analyze, transform, and process text content',
    generalSteps: [
      'Paste or type your text into the input area',
      'Most tools provide real-time processing as you type',
      'Use additional options if available (case conversion, sorting, etc.)',
      'Copy the processed text or download as a file'
    ],
    tips: [
      'Text processing is instant and happens as you type',
      'Large texts are handled efficiently in your browser',
      'Many tools offer batch processing for multiple operations',
      'Results can usually be copied directly to your clipboard'
    ]
  },
  encryption: {
    icon: <Shield className="h-6 w-6" />,
    title: 'Encryption & Encoding Tools',
    description: 'Secure your data with various encoding and hashing methods',
    generalSteps: [
      'Enter your text or data in the input field',
      'Select the desired encoding/hashing method',
      'Click process to generate the encoded/hashed result',
      'Copy the result for use in your applications'
    ],
    tips: [
      'Hashing is one-way - you cannot reverse MD5, SHA hashes',
      'Base64 is encoding, not encryption - it can be decoded',
      'All cryptographic operations happen locally for security',
      'Use strong, unique inputs for better hash security'
    ]
  },
  developer: {
    icon: <Code className="h-6 w-6" />,
    title: 'Developer Tools',
    description: 'Utilities for developers including converters and formatters',
    generalSteps: [
      'Input your code, data, or content to be processed',
      'Configure format options if available',
      'Process the content using the tool\'s functionality',
      'Copy or download the formatted/converted result'
    ],
    tips: [
      'Great for API development and data transformation',
      'JSON and XML tools preserve data structure',
      'Markdown tools support full syntax including tables and code blocks',
      'IP and network tools help with system administration'
    ]
  },
  converters: {
    icon: <Calculator className="h-6 w-6" />,
    title: 'Converter Tools',
    description: 'Convert between different number systems, formats, and data types',
    generalSteps: [
      'Enter the value you want to convert',
      'Select source and target formats/systems',
      'View the converted result instantly',
      'Copy the result or use additional conversion options'
    ],
    tips: [
      'Number base conversions support binary, octal, decimal, and hexadecimal',
      'QR codes can encode text, URLs, and other data types',
      'Timezone conversions account for daylight saving time',
      'Most converters work bidirectionally'
    ]
  }
};

export const HowToUsePage: React.FC = () => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openTool, setOpenTool] = useState<string | null>(null);
  const tools = getAllTools();

  const toggleCategory = (categoryId: string) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
  };

  const toggleTool = (toolId: string) => {
    setOpenTool(openTool === toolId ? null : toolId);
  };

  const categories = Object.keys(categoryInstructions);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Complete Guide: How to Use PrivateTools - 40+ Free Online Tools
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto mb-6">
            Comprehensive step-by-step tutorials for all PrivateTools. Master image compression, PDF merging, 
            text analysis, encryption tools, and more with our detailed instructions, pro tips, and best practices.
          </p>
          
          {/* Tool Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 max-w-4xl mx-auto mb-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">8</div>
              <div className="text-sm text-blue-700 dark:text-blue-400">Image Tools</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">5</div>
              <div className="text-sm text-red-700 dark:text-red-400">PDF Tools</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">8</div>
              <div className="text-sm text-green-700 dark:text-green-400">Text Tools</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">7</div>
              <div className="text-sm text-purple-700 dark:text-purple-400">Encryption</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">7</div>
              <div className="text-sm text-yellow-700 dark:text-yellow-400">Developer</div>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">8</div>
              <div className="text-sm text-indigo-700 dark:text-indigo-400">Converters</div>
            </div>
          </div>

          {/* SEO Content */}
          <div className="text-left max-w-5xl mx-auto mb-8 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Why PrivateTools is the Best Choice for Online File Processing
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
              PrivateTools offers 40+ completely free online tools that process your files locally in your browser. 
              Unlike other online tools that upload your files to servers, our client-side processing ensures complete 
              privacy and security. Whether you need to compress images for websites, merge PDFs for reports, analyze 
              text for content creation, or convert between different formats, our tools provide professional-grade 
              results without compromising your data privacy.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <strong className="text-gray-900 dark:text-white">🔒 100% Private:</strong> All processing happens in your browser
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">⚡ Fast & Efficient:</strong> No uploads mean faster processing
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">🆓 Completely Free:</strong> All tools with no registration required
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start Guide */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 mb-12 border border-blue-200 dark:border-blue-800">
          <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-4 flex items-center">
            <Play className="h-6 w-6 mr-2" />
            Quick Start Guide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">1</div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-2">Choose Your Tool</h3>
              <p className="text-sm text-blue-700 dark:text-blue-400">Browse our categories or search for the specific tool you need</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">2</div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-2">Upload File(s)</h3>
              <p className="text-sm text-blue-700 dark:text-blue-400">Drag & drop or select file(s) - everything stays on your device</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">3</div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-2">Process Locally</h3>
              <p className="text-sm text-blue-700 dark:text-blue-400">Click process and watch the magic happen in your browser</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">4</div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-2">Download Results</h3>
              <p className="text-sm text-blue-700 dark:text-blue-400">Download your processed file(s) or copy the results</p>
            </div>
          </div>
        </div>

        {/* Category Instructions */}
        <div className="space-y-6">
          {categories.map((categoryId) => {
            const category = categoryInstructions[categoryId as keyof typeof categoryInstructions];
            const categoryTools = tools.filter(tool => tool.category === categoryId);
            
            return (
              <div key={categoryId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => toggleCategory(categoryId)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-t-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-blue-600 dark:text-blue-400">
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {openCategory === categoryId ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </div>
                </button>

                {openCategory === categoryId && (
                  <div className="px-6 pb-6">
                    {/* General Category Instructions */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">General Steps:</h4>
                      <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
                        {category.generalSteps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                      
                      <h4 className="font-semibold text-gray-900 dark:text-white mt-4 mb-3">Tips & Best Practices:</h4>
                      <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                        {category.tips.map((tip, index) => (
                          <li key={index}>{tip}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Individual Tools */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Individual Tool Instructions:</h4>
                      {categoryTools.map((tool) => {
                        const instructions = toolInstructions[tool.id];
                        
                        return (
                          <div key={tool.id} className="border border-gray-200 dark:border-gray-600 rounded-lg">
                            <button
                              onClick={() => toggleTool(tool.id)}
                              className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                            >
                              <div>
                                <h5 className="font-medium text-gray-900 dark:text-white">{tool.name}</h5>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{tool.description}</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Link
                                  to={`/tools/${tool.id}`}
                                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Try Tool
                                </Link>
                                <div className="text-gray-400">
                                  {openTool === tool.id ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </div>
                              </div>
                            </button>

                            {openTool === tool.id && instructions && (
                              <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-600">
                                <div className="pt-3">
                                  <h6 className="font-medium text-gray-900 dark:text-white mb-2">Step-by-step Instructions:</h6>
                                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {instructions.steps.map((step, index) => (
                                      <li key={index}>{step}</li>
                                    ))}
                                  </ol>
                                  
                                  {instructions.tips.length > 0 && (
                                    <>
                                      <h6 className="font-medium text-gray-900 dark:text-white mb-2">Tips:</h6>
                                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-3">
                                        {instructions.tips.map((tip, index) => (
                                          <li key={index}>{tip}</li>
                                        ))}
                                      </ul>
                                    </>
                                  )}
                                  
                                  {instructions.supportedFormats && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      <strong>Supported formats:</strong> {instructions.supportedFormats.join(', ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Advanced Tips & Tricks */}
        <div className="mt-12 space-y-8">
          {/* Pro Tips Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-8 border border-purple-200 dark:border-purple-800">
            <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-400 mb-6 text-center">
              Pro Tips for Maximum Efficiency
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-3">⚡ Speed Optimization</h3>
                <ul className="text-sm text-purple-800 dark:text-purple-400 space-y-1">
                  <li>• Close unnecessary browser tabs before processing large files</li>
                  <li>• Use batch processing for multiple files when the tool supports it</li>
                  <li>• Desktop computers handle large files better than mobile devices</li>
                  <li>• Clear browser cache if tools load slowly</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-3">🎯 Quality Settings</h3>
                <ul className="text-sm text-purple-800 dark:text-purple-400 space-y-1">
                  <li>• Start with 80% quality for most image compression needs</li>
                  <li>• Use 90-95% for professional/print materials</li>
                  <li>• Test different settings with preview before processing</li>
                  <li>• Consider end use when choosing compression levels</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-3">💡 Workflow Tips</h3>
                <ul className="text-sm text-purple-800 dark:text-purple-400 space-y-1">
                  <li>• Bookmark frequently used tools for quick access</li>
                  <li>• Use drag-and-drop for faster file selection</li>
                  <li>• Combine tools for complex workflows (e.g., compress then convert)</li>
                  <li>• Keep original files as backup before processing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Common Use Cases */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-8 border border-blue-200 dark:border-blue-800">
            <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-6 text-center">
              Common Use Cases & Workflows
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">📸 Web Developers</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• Compress images for faster website loading</li>
                  <li>• Convert WEBP to JPG for broader compatibility</li>
                  <li>• Generate Base64 for inline CSS images</li>
                  <li>• Create QR codes for mobile app downloads</li>
                  <li>• Format JSON/XML for API development</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">📄 Office Workers</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• Merge multiple PDFs into comprehensive reports</li>
                  <li>• Convert images to PDF for document archival</li>
                  <li>• Analyze text for word count compliance</li>
                  <li>• Compress images for email attachments</li>
                  <li>• Remove duplicates from data lists</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">🎓 Students & Educators</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• Convert assignments between different formats</li>
                  <li>• Analyze essays for readability and word count</li>
                  <li>• Create QR codes for resource sharing</li>
                  <li>• Compress presentation images</li>
                  <li>• Format text for consistent styling</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Troubleshooting Guide */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg p-8 border border-orange-200 dark:border-orange-800">
            <h2 className="text-2xl font-bold text-orange-900 dark:text-orange-400 mb-6 text-center">
              Troubleshooting Common Issues
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-orange-900 dark:text-orange-400 mb-3">🔧 Technical Issues</h3>
                <div className="space-y-3 text-sm text-orange-800 dark:text-orange-400">
                  <div>
                    <strong>Tool won't load:</strong> Check internet connection, try refreshing page, clear browser cache
                  </div>
                  <div>
                    <strong>Processing fails:</strong> Verify file format support, check file isn't corrupted, try smaller file
                  </div>
                  <div>
                    <strong>Slow performance:</strong> Close other tabs, use desktop computer, ensure sufficient RAM
                  </div>
                  <div>
                    <strong>Download issues:</strong> Disable popup blockers, check download folder permissions
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-orange-900 dark:text-orange-400 mb-3">🛠️ File Issues</h3>
                <div className="space-y-3 text-sm text-orange-800 dark:text-orange-400">
                  <div>
                    <strong>Unsupported format:</strong> Check tool description for supported formats, try converting first
                  </div>
                  <div>
                    <strong>Poor quality results:</strong> Adjust quality settings, try different compression levels
                  </div>
                  <div>
                    <strong>File too large:</strong> Use image compressor first, split large PDFs, process in batches when supported
                  </div>
                  <div>
                    <strong>Missing features:</strong> Check if you're using the correct tool for your needs
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SEO Footer Content */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-8 border border-green-200 dark:border-green-800">
            <h2 className="text-2xl font-bold text-green-900 dark:text-green-400 mb-6 text-center">
              Why Choose PrivateTools for Your File Processing Needs?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl mb-3">🔒</div>
                <h3 className="font-semibold text-green-900 dark:text-green-400 mb-2">Complete Privacy</h3>
                <p className="text-sm text-green-800 dark:text-green-400">
                  All processing happens locally. No file uploads, no server storage, no privacy risks.
                </p>
              </div>
              <div>
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-semibold text-green-900 dark:text-green-400 mb-2">Lightning Fast</h3>
                <p className="text-sm text-green-800 dark:text-green-400">
                  Client-side processing eliminates upload time. Process files instantly in your browser.
                </p>
              </div>
              <div>
                <div className="text-4xl mb-3">🆓</div>
                <h3 className="font-semibold text-green-900 dark:text-green-400 mb-2">100% Free</h3>
                <p className="text-sm text-green-800 dark:text-green-400">
                  All 40+ tools completely free. No registration, no subscriptions, no hidden costs.
                </p>
              </div>
              <div>
                <div className="text-4xl mb-3">🌐</div>
                <h3 className="font-semibold text-green-900 dark:text-green-400 mb-2">Universal Access</h3>
                <p className="text-sm text-green-800 dark:text-green-400">
                  Works on any device, any browser. Desktop, tablet, mobile - access anywhere.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-8 border border-indigo-200 dark:border-indigo-800">
            <h3 className="text-xl font-semibold text-indigo-900 dark:text-indigo-400 mb-4">
              Need More Help?
            </h3>
            <p className="text-indigo-700 dark:text-indigo-400 mb-6">
              Check our comprehensive FAQ for common questions or contact us for specific assistance with any tool.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/faq"
                className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
              >
                View FAQ
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-white hover:bg-gray-50 text-indigo-600 font-semibold rounded-lg border border-indigo-600 transition-colors dark:bg-gray-800 dark:text-indigo-400 dark:border-indigo-400 dark:hover:bg-gray-700"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-blue-800 dark:text-blue-400 text-sm text-center">
            🔒 <strong>Privacy First:</strong> All processing happens locally in your browser. Your files never leave your device, ensuring complete privacy and security.
          </p>
        </div>
      </div>
    </div>
  );
};